import { Stack, TextField, IconButton, Button, Box } from '@mui/material';
import palette from 'theme/palette';
import { useEffect, useRef, useState } from 'react';
import { NotesGroup } from '../NotesGroup';
import { NotesPopover } from '../NotesPopover';
import { GroupSelect } from '@/components/common/GroupSelect';
import { Field, FieldLabel } from '@/components/ui/field';
import { NoNotesFound } from '../NoNotesFound';
import Icon from '@/units/Icon';
import { CountBadge } from '../ui/CountBadge';
import { Select, TSelectOption } from '@/components/common/SelectField';
import { MappedNoteCard } from '../card';
import { useNotesContext } from '../../hooks/useNotesContext';
import { DateGroupByType, ListGroupByType, NotesPopoverType, NotesViewType } from '../../enums';
import { WeekSwitch } from '@/components/common/WeekSwitch';
import { TNote } from '../../types';
import { useGroupNotes } from '../../hooks/useGroupNotes';
import { useFilterNotes } from '../../hooks/useFilterNotes';
import { useNotesFilterInit } from '../../hooks/useNotesFilterInit';
import { useNotesStore } from '../../stores/notes.store';
import { FAIcon } from '@packages/ui-components';
import { filterGroupHeight } from '../../data';

type Props = {
  onAddNote?: () => void;
  onNoteClick: (note: TNote) => void;
  onEditNote: (note: TNote) => void;
};

export function NotesList({ onAddNote, onNoteClick, onEditNote }: Props) {
  const { isGuest, tour, tags, selectedNoteTag, setSelectedFilterTag } = useNotesContext();
  const notes = useNotesStore((state) => state.notes);

  const [weekRange, setWeekRange] = useState<[Date, Date]>(WeekSwitch.getWeekRange());
  const [search, setSearch] = useState<string>('');
  const [openedPopover, setOpenedPopover] = useState<NotesPopoverType | null>(null);

  const {
    clearFilter,
    filters,
    setFilters,
    selectedFilterGroup,
    setSelectedFilterGroup,
    filterGroups,
    getFiltersCount,
  } = useNotesFilterInit();
  const filterAnchorRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<NotesViewType>(NotesViewType.Date);
  const [groupBy, setGroupBy] = useState<ListGroupByType | DateGroupByType>(
    view === NotesViewType.Date ? DateGroupByType.CreationDate : ListGroupByType.Status,
  );

  useEffect(() => {
    if (filters.tags?.length === 1) {
      const foundTag = tags.find((t) => t.id === filters.tags[0]);
      setSelectedFilterTag(foundTag);
    } else {
      setSelectedFilterTag(null);
    }
  }, [filters, tags, setSelectedFilterTag]);

  useEffect(() => {
    if (!tour?.tagNotesEnabled) return;

    if (selectedNoteTag) {
      setFilters({ tags: [selectedNoteTag.id], status: [] });
      setView(NotesViewType.List);
      setGroupBy(ListGroupByType.Tags);
    } else {
      setFilters({ tags: [], status: [] });
      setView(NotesViewType.Date);
      setGroupBy(DateGroupByType.CreationDate);
    }
  }, [selectedNoteTag, tour, isGuest, setFilters]);

  const groupByOptions: TSelectOption[] =
    view === NotesViewType.Date
      ? [
          {
            value: DateGroupByType.CreationDate,
            label: 'Creation Date',
            icon: <Icon.Calendar sx={{ fill: 'transparent', stroke: '#667085' }} />,
          },
        ]
      : [
          {
            value: ListGroupByType.Status,
            label: 'Status',
            icon: <Icon.Bolt sx={{ fill: 'transparent', stroke: '#667085' }} />,
          },
          {
            value: ListGroupByType.Tags,
            label: 'Tags',
            icon: <Icon.CircleTag sx={{ fill: '#667085' }} />,
            hidden: !tour.tagNotesEnabled,
          },
        ];

  const { filteredCardNotes } = useFilterNotes({
    notes,
    filters,
    searchTerm: search,
    createdAtDateRange: view === NotesViewType.Date ? weekRange : undefined,
    tags,
  });

  const noteGroups = useGroupNotes({ tags, notes: filteredCardNotes, groupBy });

  const groupFiltersCount = Object.values(filters).reduce(
    (acc, value) => (value.length > 0 ? acc + 1 : acc),
    0,
  );

  // useEffect(() => {
  //   hideElements({ hideTags: hiddenTourNotes.map((t) => t.sid), polygonsToHide: [], vsImagesToHide: [], vsVideosToHide: [], vsItemsToHide: [] });

  //   filteredTourNotes.forEach((thread) => {
  //     if (thread.tagId) return;

  //     toggleThreadVisibility(thread, true);
  //   });

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [filteredTourNotes, hiddenTourNotes, filteredCardNotes]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearch(searchTerm);
  };

  const filtersCount = getFiltersCount();

  return (
    <Stack gap="10px" p="18px" sx={{ overflow: 'auto' }}>
      <Stack direction="row" gap="10px" width="100%" ref={filterAnchorRef}>
        <TextField
          value={search}
          variant="outlined"
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <Icon.Search
                sx={{ fontSize: '20px', fontWeight: '300', marginRight: '7px', color: '#667085' }}
              />
            ),
            sx: {
              '& input::placeholder': {
                color: '#667085',
                opacity: 1,
                fontSize: '16px',
              },
            },
          }}
          inputProps={{
            sx: {
              maxHeight: '44px',
              height: '100%',
            },
          }}
          sx={{
            borderRadius: '8px',
            border: '1px solid #D1D1D1',
            background: '#FFF',
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '& fieldset': { height: '100%', top: 0 },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D0D5DD',
              borderRadius: '8px',
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              background: '#FFF',
            },
          }}
          placeholder="Search"
        />

        {!notes.length ? (
          <IconButton
            onClick={onAddNote}
            disabled={isGuest}
            id="add-button"
            sx={{
              borderRadius: '8px',
              border: '1px solid #D1D1D1',
              background: '#FFF',
              width: '44px',
              height: '44px',
              '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
              fieldset: { height: '100%', top: 0 },
            }}
          >
            <FAIcon icon="faPlus" size={16} color="#2055FF" />
          </IconButton>
        ) : (
          <>
            <IconButton
              onClick={() => setOpenedPopover(NotesPopoverType.View)}
              aria-haspopup="true"
              sx={{
                borderRadius: '8px',
                border: '1px solid #D0D5DD !important',
                background: '#fff',
                width: '44px',
                height: '44px',
                cursor: 'pointer',
              }}
            >
              <Icon.List style={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton
              id="filter-button"
              onClick={() => setOpenedPopover(NotesPopoverType.Filter)}
              aria-controls={openedPopover === NotesPopoverType.Filter ? 'filter-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openedPopover === NotesPopoverType.Filter ? 'true' : undefined}
              sx={{
                position: 'relative',
                borderRadius: '8px',
                border: '1px solid #D0D5DD !important',
                background: '#FFF',
                width: '44px',
                height: '44px',
                '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                fieldset: { height: '100%', top: 0 },
                cursor: 'pointer',
              }}
            >
              <FAIcon icon="faFilter" size={20} color="#667085" />

              {groupFiltersCount > 0 && (
                <CountBadge
                  count={groupFiltersCount}
                  sx={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '13px',
                    height: '13px',
                    color: '#fff',
                    backgroundColor: palette.light.primary.main,
                    fontSize: '10px',
                  }}
                />
              )}
            </IconButton>
          </>
        )}
      </Stack>
      <NotesPopover
        icon={<FAIcon icon="faListUl" size={16} />}
        anchorEl={filterAnchorRef.current}
        open={openedPopover === NotesPopoverType.View}
        title="Views"
        onOpenChange={(v) => {
          setOpenedPopover(v ? NotesPopoverType.View : null);
        }}
        PaperProps={{
          sx: {
            width: '340px',
          },
        }}
      >
        <Stack direction="column" width="100%" p="12px" spacing="24px">
          <Field>
            <FieldLabel tooltip="view">View</FieldLabel>
            <Select
              value={view}
              onChange={(item) => {
                setView(item.value);

                if (item.value === NotesViewType.Date) {
                  setGroupBy(DateGroupByType.CreationDate);
                } else {
                  setGroupBy(ListGroupByType.Status);
                }
              }}
              menuWidth="220px"
              options={[
                {
                  value: NotesViewType.Date,
                  label: 'Date',
                  icon: <Icon.Calendar sx={{ fill: 'transparent', stroke: '#667085' }} />,
                },
                {
                  value: NotesViewType.List,
                  label: 'List view',
                  icon: <FAIcon icon="faListUl" size={16} />,
                },
              ]}
            />
          </Field>

          <Field>
            <FieldLabel tooltip="Group by">Group by</FieldLabel>
            <Select
              value={groupBy}
              onChange={(option) => {
                setGroupBy(option.value as any);
              }}
              menuWidth="220px"
              options={groupByOptions}
            />
          </Field>
        </Stack>
      </NotesPopover>

      <NotesPopover
        icon={<FAIcon icon="faFilter" size={16} />}
        anchorEl={filterAnchorRef.current}
        open={openedPopover === NotesPopoverType.Filter}
        title="Filter"
        onOpenChange={(v) => {
          setOpenedPopover(v ? NotesPopoverType.Filter : null);
        }}
        headerStartContent={groupFiltersCount > 0 ? <CountBadge count={groupFiltersCount} /> : null}
        headerEndContent={
          Object.values(filters).some((filter) => !!filter.length) ? (
            <Button
              onClick={() => {
                clearFilter();
              }}
              disableElevation
              variant="text"
              disableRipple
              sx={{
                all: 'unset',
                color: 'var(--Gray-500, #667085)',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
                paddding: '0px !important',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'var(--Gray-500, #667085)',
                },
              }}
            >
              Clear all
            </Button>
          ) : null
        }
      >
        <GroupSelect
          mui={{
            sx: { height: filterGroupHeight[selectedFilterGroup] ?? '180px', width: '413px' },
          }}
          onGroupChange={(v) => setSelectedFilterGroup(v)}
          value={filters}
          groups={filterGroups}
          isMulti
          onChange={(v) => {
            setFilters(v);
          }}
        />
      </NotesPopover>

      {view === NotesViewType.Date && <WeekSwitch value={weekRange} onDateChange={setWeekRange} />}

      {!filteredCardNotes.length && (
        <Box sx={{ my: '20px' }}>
          {filtersCount > 0 ? (
            <NoNotesFound
              title="No results found"
              description="Try adjusting or clearing some filters to see more items."
              isButtonVisible={false}
            />
          ) : null}

          {filtersCount === 0 ? (
            <NoNotesFound
              title="No results found"
              description="Click “Add Note” to create your first note."
              isButtonVisible={false}
            />
          ) : null}
        </Box>
      )}

      {noteGroups.map((group) => {
        return (
          <NotesGroup
            key={group.key}
            groupName={group.groupName}
            notes={group.notes}
            groupKey={group.key}
            groupBy={groupBy}
            rtl={tour.client?.rtl}
          >
            {group.notes.map((note) => {
              return (
                <MappedNoteCard
                  key={note.id.toString()}
                  note={note}
                  onEdit={() => {
                    onEditNote?.(note);
                  }}
                  onClick={() => {
                    onNoteClick?.(note);
                  }}
                />
              );
            })}
          </NotesGroup>
        );
      })}
    </Stack>
  );
}
